import { Controller } from "@hotwired/stimulus"
import { getJSON, sendJSON } from "../api.js"
import { DEFAULT_TONE, TONES, iconNode } from "../tour/icons.js"
import { resolve } from "../tour/target.js"
import { closeDrawer, drawerFor, openDrawer } from "../tour/drawer.js"
import { FLAG, blockedControl, mark, unmarkAll } from "../tour/block.js"
import { h } from "../tour/dom.js"

/*
  tutorial — a step-by-step tour of a page (README §1 of docs/tutorial-component).
  Dims the page, uncovers the element a step points at (data-tour anchor) and shows a bubble next to it.

  Put on the page's root element NEXT TO the page's own controller; the values come from {% tour_values "<page>" %}:
    data-controller="lead-list tutorial"
    data-tutorial-page-value / -url-value (GET steps) / -seen-url-value (POST seen) / -autostart-value

  Step contract: { anchor, scope?, nth?, title, body, icon, tone, placement } — resolved by ../tour/target.js.
  A step whose element is not on the page is skipped.

  Autostart: autostart-delay-value ms (400) after the steps arrive, unless ?tour=off, the server says it was
  seen (autostart=false) or the per-user cache `tutorial:<page>` is set. Rows of a list render asynchronously,
  so the start polls for the anchors (poll-ms × poll-tries) and starts as soon as every step resolves — or with
  the steps that do, once the wait is over. The „Samouczek” button (tutorial#start) replays at any time.

  "Seen": POST seen-url once, when the tour is closed (finish / × / Esc) and the server did not know it yet.
  A configurator preview — `startAt(index, steps)` called on the instance inside the preview container — never reports.

  Anchor lookup scope: the document, except when this element lives inside a `[data-tour-preview]` container
  (the configurator's inline preview): then ONLY that container, so the configurator's own UI is never resolved.
  Geometry goes through CSS custom properties (--tour-top …); colours through data-tone (tour.css).
*/
export default class extends Controller {
  static values = {
    page: String,
    url: String,
    seenUrl: String,
    autostart: { type: Boolean, default: true },
    autostartDelay: { type: Number, default: 400 },
    pollMs: { type: Number, default: 250 },
    pollTries: { type: Number, default: 12 },
  }

  connect() {
    this.allSteps = []
    this.queue = []
    this.i = 0
    this.el = null
    this.target = null
    this.preview = false
    this.reported = false
    this.timer = null
    this.placeTimer = null
    this.alive = true

    this.reposition = () => this.place()
    this.onKey = (e) => this.keydown(e)

    this.loading = this.load()
    this.loading.then(() => this.scheduleAutostart())
  }

  disconnect() {
    this.alive = false
    clearTimeout(this.timer)
    this.teardown()
  }

  async load() {
    if (!this.urlValue) return
    try {
      const data = await getJSON(this.urlValue)
      this.allSteps = Array.isArray(data) ? data : []
    } catch {
      this.allSteps = []
    }
  }

  get root() {
    return this.element.closest("[data-tour-preview]") || document
  }

  // ---- seen cache (per user, per browser; the DB is the source of truth) ----
  get seenKey() { return `tutorial:${this.pageValue}` }
  seenLocally() {
    try { return !!localStorage.getItem(this.seenKey) } catch { return false }
  }
  cacheSeen() {
    try { localStorage.setItem(this.seenKey, "1") } catch { /* private mode */ }
  }

  // ---- autostart ----
  scheduleAutostart() {
    if (!this.alive || !this.autostartValue || this.seenLocally()) return
    if (new URLSearchParams(location.search).get("tour") === "off") return
    this.timer = setTimeout(() => this.awaitAnchors(0), this.autostartDelayValue)
  }

  awaitAnchors(tries) {
    this.timer = null
    if (!this.alive || this.el) return
    const ready = this.allSteps.filter((step) => resolve(this.root, step) || drawerFor(this.root, step)).length
    if (ready === this.allSteps.length || tries >= this.pollTriesValue) {
      if (ready) this.startAt(0)
      return
    }
    this.timer = setTimeout(() => this.awaitAnchors(tries + 1), this.pollMsValue)
  }

  // ---- API ----
  async start() {
    clearTimeout(this.timer)
    this.timer = null
    await this.loading
    this.startAt(0)
  }

  // `steps` given = a preview of unsaved steps (never reports "seen"); omitted = the saved steps of the page.
  startAt(index = 0, steps = null) {
    this.preview = steps !== null
    const source = steps ?? this.allSteps
    this.queue = source.filter((step) => resolve(this.root, step) || drawerFor(this.root, step))
    if (!this.queue.length) return
    this.i = Math.min(Math.max(0, index), this.queue.length - 1)
    this.build()
    this.show()
  }

  next() {
    if (this.i < this.queue.length - 1) {
      this.i += 1
      this.show()
    } else {
      this.finish()
    }
  }

  prev() {
    if (this.i > 0) {
      this.i -= 1
      this.show()
    }
  }

  finish() {
    if (!this.el) return
    const real = !this.preview
    const report = real && this.autostartValue && !this.reported && !!this.seenUrlValue
    this.teardown()
    if (real) this.cacheSeen()
    if (report) {
      this.reported = true
      sendJSON("POST", this.seenUrlValue)
    }
    if (real) this.dispatch("done", { detail: { page: this.pageValue } })
  }

  keydown(e) {
    if (!this.el) return
    if (e.key === "Escape") {
      this.finish()
    } else if (e.key === "ArrowRight" || e.key === "Enter") {
      // Enter on a focused control other than "Dalej" keeps its own meaning (Wstecz, ×)
      const button = e.target?.closest?.("button")
      if (e.key === "Enter" && button && !button.classList.contains("ls-tour-next")) return
      e.preventDefault()
      this.next()
    } else if (e.key === "ArrowLeft") {
      this.prev()
    }
  }

  // ---- DOM ----
  build() {
    this.teardown()
    this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    this.iconEl = h("span", { class: "ls-tour-ico" })
    this.stepEl = h("span", { class: "ls-tour-step" })
    this.titleEl = h("h3", { class: "ls-tour-title" })
    this.bodyEl = h("p", { class: "ls-tour-body" })
    this.dotsEl = h("span", { class: "ls-tour-dots" })
    this.backEl = h("button", { type: "button", class: "ls-tour-back" }, "Wstecz")
    this.nextEl = h("button", { type: "button", class: "ls-tour-next" }, "Dalej")
    const close = h("button", { type: "button", class: "ls-tour-x", "aria-label": "Zamknij samouczek" }, "×")
    const catcher = h("div", { class: "ls-tour-catch" })
    this.hole = h("div", { class: "ls-tour-hole" })
    this.pop = h(
      "div",
      { class: "ls-tour-pop", role: "dialog", "aria-live": "polite" },
      h("div", { class: "ls-tour-head" }, this.iconEl, this.stepEl, close),
      this.titleEl,
      this.bodyEl,
      h("div", { class: "ls-tour-foot" }, this.dotsEl, this.backEl, this.nextEl),
    )
    this.el = h("div", { class: "ls-tour", "data-tone": DEFAULT_TONE }, catcher, this.hole, this.pop)
    document.body.appendChild(this.el)

    close.addEventListener("click", () => this.finish())
    this.nextEl.addEventListener("click", () => this.next())
    this.backEl.addEventListener("click", () => this.prev())
    catcher.addEventListener("click", () => this.next())
    window.addEventListener("resize", this.reposition)
    window.addEventListener("scroll", this.reposition, { passive: true })
    document.addEventListener("keydown", this.onKey)
    // the real page only: while the tour runs no submit and no write action goes through (the stage preview has its own guard)
    if (!this.element.closest("[data-tour-preview]")) this.blockWrites()
  }

  blockWrites() {
    document.body.setAttribute(FLAG, "")
    this.guardSubmit = (e) => {
      if (e.target.closest?.(".ls-tour")) return
      e.preventDefault()
      e.stopImmediatePropagation()
    }
    this.guardClick = (e) => {
      if (!blockedControl(e.target)) return
      e.preventDefault()
      e.stopImmediatePropagation()
    }
    this.guardHover = (e) => {
      const el = blockedControl(e.target)
      if (el) mark(el)
    }
    document.addEventListener("submit", this.guardSubmit, true)
    document.addEventListener("click", this.guardClick, true)
    document.addEventListener("pointerover", this.guardHover, true)
    document.addEventListener("focusin", this.guardHover, true)
  }

  unblockWrites() {
    if (!this.guardClick) return
    document.removeEventListener("submit", this.guardSubmit, true)
    document.removeEventListener("click", this.guardClick, true)
    document.removeEventListener("pointerover", this.guardHover, true)
    document.removeEventListener("focusin", this.guardHover, true)
    this.guardSubmit = this.guardClick = this.guardHover = null
    document.body.removeAttribute(FLAG)
    unmarkAll()
  }

  teardown() {
    clearTimeout(this.openTimer)
    this.leaveDrawer() // closed with the page's own (read-only) control, before writes are allowed again
    this.unblockWrites()
    clearTimeout(this.placeTimer)
    window.removeEventListener("resize", this.reposition)
    window.removeEventListener("scroll", this.reposition)
    document.removeEventListener("keydown", this.onKey)
    const hadBubble = !!this.el
    this.el?.remove()
    this.el = null
    this.target = null
    // focus goes back where it was (never into the drawer's inputs, never lost on <body>)
    if (hadBubble && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true })
    this.returnFocus = null
  }

  /** close the drawer this tour opened (the page's way) */
  leaveDrawer() {
    const drawer = this.openedDrawer
    this.openedDrawer = null
    if (drawer) closeDrawer(drawer)
  }

  show() {
    const step = this.queue[this.i]
    this.target = resolve(this.root, step)
    if (this.target && this.openedDrawer && !this.openedDrawer.contains(this.target)) this.leaveDrawer() // left the drawer's steps
    if (!this.target) {
      const drawer = drawerFor(this.root, step)
      if (drawer && drawer !== this.openedDrawer) {
        if (this.openedDrawer) this.leaveDrawer()
        if (openDrawer(drawer)) {
          // the page opens its own drawer; wait until the anchor is there, then show the bubble
          this.openedDrawer = drawer
          this.nextEl.focus({ preventScroll: true }) // the click must not leave the focus inside the drawer
          this.waitFor(step, 0)
          return
        }
      }
    }
    if (!this.target) {
      // the page changed since the tour started: drop the step instead of pointing at nothing
      this.queue.splice(this.i, 1)
      if (!this.queue.length) {
        this.finish()
        return
      }
      this.i = Math.min(this.i, this.queue.length - 1)
      this.show()
      return
    }
    const count = this.queue.length
    this.el.dataset.tone = TONES[step.tone] ? step.tone : DEFAULT_TONE
    this.iconEl.replaceChildren(iconNode(step.icon))
    this.stepEl.textContent = `Krok ${this.i + 1} z ${count}`
    this.titleEl.textContent = step.title || ""
    this.bodyEl.textContent = step.body || ""
    this.dotsEl.replaceChildren(
      ...this.queue.map((_, k) => h("i", { class: `ls-tour-dot${k === this.i ? " is-on" : k < this.i ? " is-done" : ""}` })),
    )
    this.backEl.hidden = this.i === 0
    this.nextEl.textContent = this.i === count - 1 ? "Gotowe" : "Dalej"

    const rect = this.target.getBoundingClientRect()
    const top = window.scrollY + rect.top - Math.max(80, (window.innerHeight - rect.height) / 2)
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" })
    this.place()
    clearTimeout(this.placeTimer)
    this.placeTimer = setTimeout(() => this.place(), 350)
    this.nextEl.focus({ preventScroll: true })
  }

  /** poll for a step's anchor after a drawer was opened; give up (skip the step) after ~1 s */
  waitFor(step, tries) {
    clearTimeout(this.openTimer)
    if (!this.el || this.queue[this.i] !== step) return
    if (resolve(this.root, step)) return this.show()
    if (tries >= 16) {
      this.leaveDrawer()
      this.queue.splice(this.i, 1)
      if (!this.queue.length) return this.finish()
      this.i = Math.min(this.i, this.queue.length - 1)
      return this.show()
    }
    this.openTimer = setTimeout(() => this.waitFor(step, tries + 1), 60)
  }

  place() {
    if (!this.el || !this.target) return
    const pad = 6
    const gap = 14
    const rect = this.target.getBoundingClientRect()
    const hole = this.hole.style
    hole.setProperty("--tour-top", `${rect.top - pad}px`)
    hole.setProperty("--tour-left", `${rect.left - pad}px`)
    hole.setProperty("--tour-width", `${rect.width + pad * 2}px`)
    hole.setProperty("--tour-height", `${rect.height + pad * 2}px`)

    const popW = this.pop.offsetWidth
    const popH = this.pop.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight
    let below = (this.queue[this.i].placement || "bottom") === "bottom"
    if (below && rect.bottom + pad + gap + popH > vh) below = false
    if (!below && rect.top - pad - gap - popH < 0) below = true
    const top = below ? rect.bottom + pad + gap : rect.top - pad - gap - popH
    const left = Math.min(Math.max(12, rect.left + rect.width / 2 - popW / 2), vw - popW - 12)
    const pop = this.pop.style
    pop.setProperty("--tour-top", `${top}px`)
    pop.setProperty("--tour-left", `${left}px`)
    this.pop.dataset.side = below ? "bottom" : "top"
    pop.setProperty("--arrow-x", `${Math.min(Math.max(22, rect.left + rect.width / 2 - left), popW - 22)}px`)
  }
}
